import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaRegCircleDot } from "react-icons/fa6";
import api from "../utils/axiosInstance";
import ReplacePopUp from "./ReplacePopUp";
import ProductQuickView from "./ProductQuickView";
import "../index.css";

const DietIcon = ({ type, size = 16 }) => {
  switch (type) {
    case "Vegetarian":
    case "Veg":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          style={{ border: "1px solid green", borderRadius: 3 }}
        >
          <rect width="100" height="100" fill="white" />
          <circle cx="50" cy="50" r="30" fill="green" />
        </svg>
      );

    case "Non-Vegetarian":
    case "Non-Veg":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          style={{ border: "1px solid red", borderRadius: 3 }}
        >
          <rect width="100" height="100" fill="white" />
          <polygon points="50,20 80,80 20,80" fill="red" />
        </svg>
      );

    case "Egg":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          style={{ border: "1px solid gold", borderRadius: 3 }}
        >
          <rect width="100" height="100" fill="white" />
          <circle cx="50" cy="50" r="30" fill="gold" />
        </svg>
      );

    default:
      return null;
  }
};

const ProductCard = ({
  product,
  bestSeller,
  user,
  setUser,
  variant = "food",
}) => {
  const [loading, setLoading] = useState(false);
  const [shopName, setShopName] = useState("");
  const [showReplacePopup, setShowReplacePopup] = useState(false);
  const [newProduct, setNewProduct] = useState("");
  const [openQuickView, setOpenQuickView] = useState(false);

  const cartKey = variant === "grocery" ? "groceryCart" : "foodCart";

  const cartItem = user?.[cartKey]?.find(
    (i) => i.productId?.toString() === product?._id?.toString(),
  );

  const addProductToCart = async () => {
    if (loading) return;
    if (!user) return toast.warning("Please login first");

    setLoading(true);
    try {
      const { data } = await api.post(
        `/api/cart/addToCart`,
        { productId: product._id, cartKey },
        {
          withCredentials: true,
        },
      );
      if (!data.success) throw new Error(data.message);
      toast.success("Product added to cart!");
      setUser((prev) => ({
        ...prev,
        [cartKey]: [...prev[cartKey], { productId: product._id, quantity: 1 }],
      }));
    } catch (err) {
      if (err.response?.data?.type === "DIFFERENT_SHOP") {
        setNewProduct(product._id);
        setShopName(err.response?.data?.shopName || "Other Shop");
        setShowReplacePopup(true);
      } else {
        toast.error(err.response?.data?.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleIncrement = async (productId) => {
    if (loading) return;

    // Optimistic UI update
    setUser((prev) => ({
      ...prev,
      [cartKey]: prev[cartKey].map((i) =>
        i.productId === productId ? { ...i, quantity: i.quantity + 1 } : i,
      ),
    }));

    try {
      const { data } = await api.post(
        `/api/cart/incrementQty`,
        { productId, cartKey },
        { withCredentials: true },
      );
      if (!data.success) throw new Error(data.message);
    } catch {
      toast.error("Could not update quantity");
    }
  };

  const handleDecrement = async (productId) => {
    if (loading) return;
    const item = cartItem;
    if (!item) return;

    if (item.quantity === 1) return removeFromCart(productId);

    // Optimistic UI update
    setUser((prev) => ({
      ...prev,
      [cartKey]: prev[cartKey].map((i) =>
        i.productId === productId ? { ...i, quantity: i.quantity - 1 } : i,
      ),
    }));

    try {
      const { data } = await api.post(
        `/api/cart/decrementQty`,
        { productId, cartKey },
        { withCredentials: true },
      );
      if (!data.success) throw new Error(data.message);
    } catch {
      toast.error("Could not update quantity");
    }
  };

  const removeFromCart = async (productId) => {
    if (loading) return;
    const prevCart = [...(user?.[cartKey] || [])];

    // Optimistic UI update
    setUser((prev) => ({
      ...prev,
      [cartKey]: prev[cartKey].filter((i) => i.productId !== productId),
    }));

    setLoading(true);
    try {
      const { data } = await api.post(
        `/api/cart/removeFromCart`,
        { productId, cartKey },
        { withCredentials: true },
      );
      if (!data.success) throw new Error(data.message);
      toast.success("Product removed from cart!");
    } catch (err) {
      // rollback
      setUser((prev) => ({ ...prev, [cartKey]: prevCart }));
      toast.error(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const replaceCart = async () => {
    try {
      setLoading(true);
      const { data } = await api.post(
        "/api/cart/replaceCart",
        { productId: product._id, cartKey },
        { withCredentials: true },
      );
      if (!data.success) throw new Error(data.message);

      toast.success("Cart updated successfully!");
      setShowReplacePopup(false);
      setUser((prev) => ({
        ...prev,
        [cartKey]: data.cart,
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (showReplacePopup) {
    return (
      <ReplacePopUp
        onCancel={() => setShowReplacePopup(false)}
        shopName={shopName}
        currShopName={product.shopName}
        newProduct={newProduct}
        replaceCart={replaceCart}
        loading={loading}
      />
    );
  }

  return (
    <>
      {/* PRODUCT CARD */}
      <div
        onClick={() => {
          setOpenQuickView(true);
        }}
        className="
          relative rounded-2xl overflow-hidden
          bg-white/80 dark:bg-gray-800/80
          backdrop-blur-md
          shadow-md hover:shadow-red-300/30
          transition-all duration-300
          hover:-translate-y-1
          active:scale-[0.97]
        "
      >
        {/* 🎁 DISCOUNT TAG */}
        {product.basePrice > product.price && (
          <span className="absolute top-0 left-2 bg-yellow-500 text-white text-xs text-center font-bold px-2 py-2 rounded-tr-lg rounded-bl-lg shadow-lg z-20">
            {Math.round(
              ((product.basePrice - product.price) / product.basePrice) * 100,
            )}
            %<p>OFF</p>
          </span>
        )}

        {product?.shop?.shopDiscount > 0 && (
          <span className="absolute top-0 left-2 bg-gradient-to-r from-red-600 to-orange-500 text-white text-xs text-center font-bold px-2 py-2 rounded-tr-lg rounded-bl-lg shadow-xl z-20">
            {product.shop.shopDiscount}%<p>OFF</p>
          </span>
        )}

        {/* 🖼 IMAGE */}
        <div className="relative overflow-hidden">

          {bestSeller && (
            <span className="absolute top-0 left-1/2 -translate-x-1/2 bg-yellow-400 text-white text-xs font-bold px-2 py-1 rounded-b-lg z-20">
              Bestseller
            </span>
          )}

          <img
            src={
              product.images?.[0] ||
              "/noImage.jpg"
            }
            alt={product.name}
            className="w-full h-36 object-cover transition-transform duration-500 hover:scale-110"
          />

          {product.dietType && (
            <div className="absolute top-2 right-2 z-20">
              <DietIcon type={product.dietType} />
            </div>
          )}

          {/* 🚫 OUT OF STOCK */}
          {!product.inStock && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-30">
              <span className="text-white font-bold text-sm">
                Not Available
              </span>
            </div>
          )}

          {/* 🛒 CTA */}
          {product.inStock &&
            (loading ? (
              <div className="absolute bottom-0 w-full text-center text-xs py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white z-20">
                Adding...
              </div>
            ) : (
              <div className="absolute bottom-0 w-full z-20">
                {cartItem ? (
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-evenly text-white py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDecrement(product._id);
                      }}
                    >
                      −
                    </button>

                    <span className="text-sm font-semibold">
                      {cartItem.quantity}
                    </span>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleIncrement(product._id);
                      }}
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addProductToCart();
                    }}
                    className="w-full py-1 text-xs font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:brightness-110"
                  >
                    Add
                  </button>
                )}
              </div>
            ))}
        </div>

        {/* 📝 INFO */}
        <div className="p-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {product.name.substr(0, 30)}
            <span className="text-green-500">
              {product.name.length > 30 && " ...More"}
            </span>
          </h3>

          {variant === "food" && (
            <p className="text-xs text-red-500 mt-1">{product.shopName}</p>
          )}

          {/* 💰 PRICE */}
          <div className="mt-1 text-sm">
            {product.basePrice > product.price ? (
              <p>
                <span className="line-through text-gray-400 mr-2">
                  ₹{product.basePrice}
                </span>
                <span className="text-green-500 font-semibold">
                  ₹{product.price}
                </span>
              </p>
            ) : product?.shop?.shopDiscount > 0 ? (
              <p>
                <span className="line-through text-gray-400 mr-2">
                  ₹{product.price}
                </span>
                <span className="text-red-500 font-semibold">
                  ₹
                  {(
                    product.price -
                    (product.price * product.shop.shopDiscount) / 100
                  ).toFixed(2)}
                </span>
              </p>
            ) : (
              <p className="text-green-500 font-semibold">₹{product.price}</p>
            )}
          </div>
        </div>
      </div>

      {/* QUICK VIEW */}
      <ProductQuickView
        open={openQuickView}
        onClose={() => setOpenQuickView(false)}
        product={product}
        cartItem={cartItem}
        loading={loading}
        onAdd={addProductToCart}
        onInc={() => handleIncrement(product._id)}
        onDec={() => handleDecrement(product._id)}
        variant={variant}
        dietType={product.dietType}
        DietIcon={DietIcon}
        shopDiscount={product?.shop?.shopDiscount}
      />
    </>
  );
};

export default ProductCard;
